import { useRequest } from 'ahooks'
import { Alert, Button, DatePicker, Input, InputNumber, Progress, Table, TableColumnsType } from 'antd'
import { every } from 'lodash'
import moment from 'moment'
import React, { useRef } from 'react'
import { DndProvider, useDrag, useDrop } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { useTranslation } from 'react-i18next'

import { StageCompleter, StageSearch, UserAvatar, UsersSearch, getPointPermission } from '@/components'
import { PAGE_RESOURCE_POINTS_ENUM, Project, ProjectStage } from '@/schemas'

import { StageAdd } from './stage-add.component'
import { StageDelete } from './stage-delete.component'
import { StageEdit } from './stage-edit.component'
import { StageTemplate } from './stage-template.component'

interface ProjectStagesContainerProps {
  project: Project
  refreshProject: () => Promise<Project>
}

const reorder = (res: ProjectStage[], sourceIndex: number, targetIndex: number) => {
  // const res = Array.from(list)
  const [item] = res.splice(sourceIndex, 1)
  res.splice(targetIndex, 0, item)
  return res.map((r, index) => ({
    id: r.id,
    order: index,
  }))
}

export const ProjectStagesContainer: React.FC<ProjectStagesContainerProps> = ({ project, refreshProject }) => {
  const { t, i18n } = useTranslation()
  const projectId = project.selfId
  const [isAdd, setIsAdd] = React.useState(false)
  const { canCreate, canUpdate, canDelete } = getPointPermission(
    PAGE_RESOURCE_POINTS_ENUM.阶段与任务,
    project.permissions
  )

  const {
    data: stages,
    loading,
    refresh,
  } = useRequest<ProjectStage[]>(
    {
      url: '/api/project/getProjectStages',
      params: {
        selfId: projectId,
      },
    },
    {
      formatResult: (data) =>
        data?.map((s) => ({
          ...s,
          name: i18n.resolvedLanguage === 'zh' ? s.name : s.ename,
        })),
    }
  )

  const f = (title: string, a: string | null, b: string | null) => {
    const fmt = (d: string | null) => d && moment(d).format('l')
    return (
      <div className='flex text-xs justify-center'>
        <div className='flex-shrink-0 text-right min-w-20'>{title}：</div>
        <div className='text-center w-70px'>{fmt(a)}</div>
        <div className='px-4px'>-</div>
        <div className='text-center w-70px'>{fmt(b)}</div>
      </div>
    )
  }

  let columns = [
    {
      title: t('stage.owner'),
      width: 150,
      render: (d: ProjectStage) => {
        return <UserAvatar size='small' showName email={d.email} />
      },
      renderEdit: (d: ProjectStage, onChange: any) => {
        return (
          <UsersSearch
            style={{ width: 130 }}
            placeholder={t('owner')}
            open
            autoFocus
            value={d.email}
            onChange={(email) => onChange({ email: email || null })}
          />
        )
      },
    },
    {
      title: t('stage.name'),
      width: 170,
      render: (d: ProjectStage) => {
        return (
          <div className='space-between flex items-center'>
            <span>{d.name}</span>
            {canDelete && (
              <StageDelete
                stage={d}
                onSuccess={() => {
                  refresh()
                  refreshProject()
                }}
              />
            )}
          </div>
        )
      },
      renderEdit: (record: ProjectStage, onChange: any) => {
        return (
          <StageSearch
            autoFocus
            defaultOpen
            style={{ width: '100%' }}
            value={record.name}
            placeholder={t('stage.tip')}
            onChange={(name: string) => {
              onChange({ name: (name || '').trim(), error: (name || '').trim() === '' ? t('stage.nulltip') : '' })
            }}
          />
        )
      },
    },
    {
      title: t('stage.description'),
      render: (d: ProjectStage) => d.description,
      renderEdit: (record: ProjectStage, onChange: any) => {
        return (
          <Input autoFocus value={record.description} onChange={(e) => onChange({ description: e.target.value })} />
        )
      },
    },
    {
      title: t('progress'),
      align: 'center',
      width: 80,
      render: (d: ProjectStage) => <Progress width={26} type='circle' size='small' percent={d.progress} />,
      renderEdit: (record: ProjectStage, onChange: any) => {
        return (
          <InputNumber
            style={{ width: 80 }}
            autoFocus
            min={0}
            max={100}
            value={record.progress}
            onChange={(progress) => onChange({ progress: Math.min(100, Math.max(0, progress)) })}
          />
        )
      },
    },
    {
      title: t('weight'),
      align: 'center',
      width: 70,
      render: (d: ProjectStage) => d.weight,
      renderEdit: (record: ProjectStage, onChange: any) => {
        return (
          <InputNumber
            style={{ width: 60 }}
            autoFocus
            min={0}
            max={10}
            value={record.weight}
            onChange={(weight) => onChange({ weight: Math.min(10, Math.max(0, weight)) })}
          />
        )
      },
    },
    {
      title: t('investment'),
      align: 'center',
      width: 90,
      render: (d: ProjectStage) => (
        <span>
          {(d.userStages || [])
            .map((userStage) => userStage.dates || [])
            .flat()
            .reduce((prev, curr) => {
              return prev + curr.count
            }, 0)}
        </span>
      ),
    },
    {
      title: t('cycle'),
      align: 'center',
      width: 350,
      render: (d: ProjectStage) => (
        <>
          {f(t('plan'), d.planStartDate, d.planEndDate)}
          {f(t('actual'), d.actualStartDate, d.actualEndDate)}
        </>
      ),
      renderEdit: (d: ProjectStage, onChange: any) => {
        const f = 'YYYY-MM-DD'
        const datePicker = (key: string) => (
          <DatePicker
            style={{ width: 110 }}
            size='small'
            suffixIcon={null}
            value={d[key] ? moment(d[key]) : undefined}
            onChange={(date) => {
              const value = date ? date.format(f) : null
              const autoUpdate = {}
              if (key === 'actualStartDate' && !d.planStartDate) {
                autoUpdate['planStartDate'] = value
              }
              if (key === 'actualEndDate' && !d.planEndDate) {
                autoUpdate['planEndDate'] = value
              }
              onChange({
                ...autoUpdate,
                [key]: value,
              })
            }}
          />
        )
        return (
          <div className='space-y-1'>
            <div>
              <span className='text-right min-w-20 inline-block'>{t('plan')}: </span>
              <span className='w-1 inline-block'></span>
              {datePicker('planStartDate')} - {datePicker('planEndDate')}
            </div>
            <div>
              <span className='text-right min-w-20 inline-block'>{t('actual')}: </span>
              <span className='w-1 inline-block'></span>
              {datePicker('actualStartDate')} - {datePicker('actualEndDate')}
            </div>
          </div>
        )
      },
    },
    {
      title: t('delay'),
      width: 70,
      align: 'right',
      render: (d: ProjectStage) => {
        // 延期 = 实际 || 当前 - 计划
        return (
          <div>
            {Math.max(moment(d.actualEndDate || undefined).diff(d.planEndDate || undefined, 'days'), 0) +
              ' ' +
              t('days')}
          </div>
        )
      },
    },
  ] as TableColumnsType<ProjectStage>

  if (canUpdate) {
    columns.push({
      title: '',
      align: 'center',
      width: 40,
      render: (d: ProjectStage) => {
        return d.progress === 100 ? (
          <div />
        ) : (
          <StageCompleter
            stage={d}
            onSuccess={async () => {
              await refresh()
              await refreshProject()
            }}
          />
        )
      },
    } as TableColumnsType<ProjectStage>[0])
  }

  columns = columns.map((col: any) => {
    return {
      ...col,
      onCell: (record: ProjectStage) => ({
        record,
        renderData: col.render,
        renderEdit: canUpdate && col.renderEdit,
        onSuccess: async () => {
          await refresh()
          if (['进度', '权重'].includes(col.title)) {
            await refreshProject()
          }
        },
      }),
    }
  })

  const summary = isAdd
    ? () => (
        <StageAdd
          selfId={projectId}
          onSuccess={async () => {
            await refresh()
            setIsAdd(false)
            await refreshProject()
          }}
          onClose={() => setIsAdd(false)}
        />
      )
    : undefined

  const hint = canUpdate && (stages || []).length > 0 && every(stages, (s) => !s.planStartDate) && (
    <Alert className='inline-flex' showIcon message={t('stage time tip')} />
  )

  const { run: updateOrder } = useRequest(
    ({ children }) => ({
      url: '/api/project/updateProjectStageOrder',
      method: 'post',
      data: {
        selfId: projectId,
        children,
      },
    }),
    {
      manual: true,
      onSuccess: refresh,
    }
  )

  const DraggableBodyRow = ({ index, record, className, style, ...restProps }: any) => {
    if (!record) return null
    const ref = useRef()

    const [{ isOver, dropClassName }, drop] = useDrop({
      accept: record.selfId,
      collect: (monitor: any) => {
        const item = monitor.getItem()
        if (!item) return {}
        const { index: dragIndex } = monitor.getItem()
        if (dragIndex === index) {
          return {}
        }
        return {
          isOver: monitor.isOver(),
          dropClassName: dragIndex < index ? ' drop-over-downward' : ' drop-over-upward',
        }
      },
      drop: async (item: any) => {
        if (item.index === index) return
        if (!stages) return
        let sendOrder = []
        sendOrder = reorder(stages, item.index, index)

        await updateOrder({ children: sendOrder })
      },
    })

    const [, drag] = useDrag({
      type: record.selfId,
      canDrag: canUpdate,
      item: { index },
      collect: (monitor) => ({ isDragging: monitor.isDragging() }),
    })

    drop(drag(ref))

    return (
      <tr
        ref={ref}
        className={`${className}${isOver ? dropClassName : ''}`}
        style={{ cursor: canUpdate ? 'pointer' : 'auto', ...style }}
        {...restProps}
      />
    )
  }

  return (
    <div className='space-y-16px'>
      {hint}
      <DndProvider backend={HTML5Backend}>
        <Table
          rowClassName='group'
          rowKey='id'
          size='small'
          loading={loading}
          columns={columns}
          locale={{ emptyText: t('stage.nodata') }}
          dataSource={stages || []}
          components={{
            body: {
              cell: StageEdit,
              row: DraggableBodyRow,
            },
          }}
          summary={summary}
          pagination={false}
          onRow={(record, index) => ({
            index,
            record,
            onError: () => {},
          })}
        />
      </DndProvider>
      {canCreate && (
        <div className='space-x-12px'>
          <StageTemplate selfId={projectId} project={project} oldStages={stages || []} refresh={refresh} />
          <Button type='primary' onClick={() => setIsAdd(true)}>
            {t('stage.add')}
          </Button>
        </div>
      )}
    </div>
  )
}
